// Background-brightness analysis + §4.5 variant whitelist.
// Ports core/renderer/doit_overlay/background.py.

import { VARIANT_CHOICES, type Variant } from "./constants.generated.js";

/**
 * Mean luminance of a 64×64 downsample > 128 → light bg.
 *
 * Uses an OffscreenCanvas to downsample without polluting the main DOM.
 * Matches Python: avg of 0.299*R + 0.587*G + 0.114*B on a 64x64 resize.
 */
export async function analyzeBgIsLight(source: ImageBitmap | HTMLImageElement): Promise<boolean> {
  const SIZE = 64;
  const canvas = new OffscreenCanvas(SIZE, SIZE);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("could not acquire 2d context for bg analysis");
  }
  ctx.drawImage(source, 0, 0, SIZE, SIZE);
  const img = ctx.getImageData(0, 0, SIZE, SIZE);
  const px = img.data;
  let total = 0;
  for (let i = 0; i < px.length; i += 4) {
    total += 0.299 * px[i]! + 0.587 * px[i + 1]! + 0.114 * px[i + 2]!;
  }
  const avg = total / (SIZE * SIZE);
  return avg > 128;
}

/** §4.5 — light bg ⇒ color/black; dark bg ⇒ inverted variants. */
export function allowedVariants(isLightBg: boolean): Variant[] {
  if (isLightBg) {
    return ["color", "black"];
  }
  return ["dark_standard", "inverted", "kanji_inverted"];
}

/** First variant in the allowed list for the image's background brightness. */
export async function autoPickVariant(
  source: ImageBitmap | HTMLImageElement,
): Promise<Variant> {
  const isLight = await analyzeBgIsLight(source);
  return allowedVariants(isLight)[0]!;
}

/**
 * Resolve a user-supplied variant string (incl. 'auto') to a concrete variant.
 * Emits console.warn when the user explicitly picks a non-§4.5-compliant variant.
 */
export async function resolveLogoVariant(
  source: ImageBitmap | HTMLImageElement,
  requested: string,
): Promise<Variant> {
  if (requested === "auto") {
    return autoPickVariant(source);
  }
  if (!VARIANT_CHOICES.includes(requested)) {
    throw new Error(`unknown variant ${JSON.stringify(requested)}`);
  }
  const isLight = await analyzeBgIsLight(source);
  if (!allowedVariants(isLight).includes(requested as Variant)) {
    const bg = isLight ? "light" : "dark";
    console.warn(
      `variant=${requested} is not recommended for ${bg} bg (see §4.5); rendering anyway.`,
    );
  }
  return requested as Variant;
}
